// app/components/ContactListControls/index.js
import React, { useEffect, useRef, useState } from 'react';
import pluralize from 'pluralize';
import classNames from 'classnames';
import Button, { OutlinedButton } from 'components/Button';
import ActionDropdownMenu from 'components/base/ActionDropdownMenu';
import SVG from 'components/base/SVG';
import { isPropStream } from 'utils/brand';
import { defaultGroupId } from 'reducers';
import { ContactGroupTypes } from 'data/contacts';
import useViewportSize from 'utils/useViewportSize';
import Tooltip from 'app/components/Tooltip';
import { contactGroupPath } from 'app/routes';
import getButtonVisibility from './getButtonVisibility';

import css from '../../Property/MyPropertiesControls/style.scss';


const ContactListControls = ({
  // flags / values
  loading,
  selected,
  context,
  append,
  pabEnabled,
  size,
  retrieved,
  status,
  isDefaultGroup,
  // handlers
  openImport,
  handleExportClick,
  handleAddToGroupClick,
  handleRenameGroupClick,
  deleteContacts,
  deleteGroup,
  handleGenerateMailingLabels,
  handleSkipTraceClick,
  openBatchDialerModal,
  // permission flags
  showPushToBatchDialer,
}) => {
  const { width } = useViewportSize();
  const {
    hideStatusText,
    hideImportExportText,
    hideNewContactText,
    hideSkipTraceText,
  } = getButtonVisibility(width, append);

  const groupName = context.getIn(['info', 'name']);
  const titleRef = useRef(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  useEffect(() => {
    const el = titleRef.current;
    setIsTitleTruncated(!!el && el.scrollWidth > el.clientWidth);
  }, [groupName, selected, width]);

  const statusClass = status.toLowerCase();
  
  // Items for the Actions dropdown
  const actionItems = [
    // Push to BatchDialer - with custom SVG icon
    isPropStream() ? {
      label: 'Push to BatchDialer',
      icon: <SVG icon="push-to-batch" />,
      onSelect: openBatchDialerModal,
      disabled: !selected
    } : null,

    // Generate Mailing Labels
    {
      label: 'Generate Mailing Labels',
      icon: <SVG icon="generate-mail" />,
      onSelect: handleGenerateMailingLabels,
      disabled: !selected
    },

    // Divider
    { divider: true },

    // Add to Group - with sub-items (expands in-place)
    {
      label: 'Add to Group',
      icon: <SVG icon="add-to-list" />,
      disabled: !selected,
      onSelect: handleAddToGroupClick
    },

    // Rename Group - conditional
    context.get('id') !== defaultGroupId ? {
      label: 'Rename This Group',
      icon: <SVG icon="icon-edit" />,
      onSelect: handleRenameGroupClick,
    } : null,

    // Delete Group - conditional
    context.get('id') !== defaultGroupId && !append ? {
      label: 'Delete This Group',
      icon: <SVG icon="delete-green" />,
      onSelect: deleteGroup,
    } : null,

    // Divider
    selected && !append ? { divider: true } : null,

    // Delete Selection
    selected && !append ? {
      label: 'Delete Selection',
      icon: <SVG icon="delete-red" />,
      onSelect: deleteContacts,
      disabled: !selected
    } : null,
  ];

  return (
    <div className={css.controlsContainer}>
      <div className={css.infoGroup}>
        <div>
          {!selected ? <div /> : <div className={css.selected}>{selected} Selected</div>}
        </div>
       
        <div className={css.countContainer} style={{ flexDirection: selected ? 'row' : 'column' }}>
          {isTitleTruncated ? (
            <Tooltip content={groupName} position="bottom" arrowOffset="50%" yOffset={-10}>
              <div ref={titleRef} className={classNames({[css.title]: selected, [css.titleSelected]: !selected})}>
                {groupName}
              </div>
            </Tooltip>
          ) : (
            <div ref={titleRef} className={classNames({[css.title]: selected, [css.titleSelected]: !selected})}>
              {groupName}
            </div>
          )}
          <div className={classNames({[css.titleQuantity]: selected, [css.titleQuantitySelected]: !selected})}>
            ({retrieved}{retrieved < size ? ` of ${size}` : ''})
          </div>
        </div>
      </div>

      {!append ? null : (
        <div className={css.status}>
          {hideStatusText ? null : <div className={css.skipstraceheader}>Skip Tracing Status</div>}
           <div className={css.statusIndicator}>
              <span className={classNames(css.statusDot, css[statusClass])}></span>
              {hideStatusText ? null : <span className={css.skipstracestatus}>{status}</span>}
            </div>
        </div>
      )}

      <div className={css.controlWrapper}>
        <div className={css.controlGroup}>
          <OutlinedButton
            loading={loading}
            icon="myproperty-import"
            onlyIcon={hideImportExportText}
            text="Import List"
            onClick={openImport}
          />
          <OutlinedButton
            icon="myproperty-export"
            onlyIcon={hideImportExportText}
            loading={loading}
            disabled={!selected}
            text="Export"
            onClick={handleExportClick}
          />
          <OutlinedButton
            icon="add"
            loading={loading}
            url={contactGroupPath('0/new')}
            onlyIcon={hideNewContactText}
            text="New Contact"
          />
        </div>

        <div className={css.controlGroup}>
          <OutlinedButton
            icon="myproperty-skip-trace"
            loading={loading}
            orangeTheme
            onlyIcon={hideSkipTraceText}
            disabled={!selected}
            text="Skip Trace"
            onClick={handleSkipTraceClick}
          />
          <ActionDropdownMenu
            text="Actions"
            icon="iconCheckSquare"
            loading={loading}
            showHoverState={!selected}
            hoverMessage="Select Item(s) First"
            items={actionItems}
            trigger={<OutlinedButton rightIcon="myproperty-arrow-down" solidPrimary loading={loading} text="Actions" />}
          />
        </div>
      </div>
    </div>
  );
};

export default ContactListControls;